<template>
  <payment-container v-model="_selected" v-bind="containerProps">
    <img src="~/assets/images/integrations/square.png" class="max-h-8 max-w-14 opacity-85">

    <template #control>
      <div v-if="active" class="mt-4">
        <o-alert v-if="isStatusAuthError" show :title="$t('integration.needsReconnecting')" class="text-sm" variant="warning">
          <p>
            {{ $t('integration.square.reconnectInfo') }}
          </p>
        </o-alert>

        <o-alert v-if="isStatusSetupIncomplete" show :title="$t('integration.setupIncomplete')" class="text-sm" variant="warning">
          <p>
            {{ $t('integration.square.setupIncompleteInfo') }}
          </p>
        </o-alert>

        <form id="payment-form">
          <div id="card-container" />
        </form>

        <div id="payment-status-container" />

        <template v-if="isStatusConnected">
          <div v-if="!loaded && !error" class="w-full text-center my-8">
            <o-loader :label="$t('integration.square.loading')" size="20px" class="mx-auto mb-3" />
            <p class="text-sm text-gray-700 dark:text-gray-200">
              {{ $t('integration.square.loading') }}
            </p>
          </div>

          <div v-if="error" class="w-full my-8 text-sm">
            <o-alert variant="danger" :title="error" show />
          </div>
        </template>
      </div>
    </template>
  </payment-container>
</template>

<script setup lang="ts">
import { v4 as uuid } from 'uuid'

import {
  OAlert,
} from '@/components/elements'

import {
  CheckboxField,
} from '@/components/fields'

import PaymentContainer from '@/components/order/elements/payments/PaymentContainer.vue'
import { Integration } from '@/_api/models/integration'

const { $api, $i18n, $config } = useNuxtApp()

interface Props {
  order: any
  amount: string | number
  tip?: number | string | null
  option: any
  selected?: string | null
}

const props = withDefaults(defineProps<Props>(), {
  tip: null,
  selected: null,
})

const emit = defineEmits<{
  (e: 'update:selected', value: string): void
}>()

const _selected = computed({
  get: () => props.selected,
  set: (value: string) => emit('update:selected', value),
})

const active = computed(() => props.option.guid === props.selected)
const integration = computed(() => new Integration(props.option.data))

const paymentsInstance = ref<any>(null)
const card = ref<any>(null)
const rememberCard = ref(false)
const loaded = ref(false)
const error = ref<string | false>(false)
const billingContact = ref({
  familyName: '',
  givenName: '',
  email: '',
  addressLines: [''],
  city: '',
  region: '',
})

const containerProps = computed(() => ({
  label: $i18n.t('components.order.elements.payments.integration.square.manual.label'),
  info: $i18n.t('integration.square.label'),
  option: props.option,
}))

const isStatusDisconnected = computed(() => integration.value.isStatusDisconnected)
const isStatusConnected = computed(() => integration.value.isStatusConnected)
const isStatusSetupIncomplete = computed(() => integration.value.isStatusSetupIncomplete)
const isStatusAuthError = computed(() => integration.value.isStatusAuthError)

watch(active, (newActive) => {
  if (newActive && isStatusConnected.value) {
    load()
  }
}, { immediate: true })

onMounted(() => {
  if (props.order.customer) {
    const customer = props.order.customer

    if (customer.address) {
      billingContact.value.addressLines[0] = customer.address
    }
    if (customer.address2) {
      billingContact.value.addressLines[1] = customer.address2
    }

    if (customer.suburb && customer.city) {
      billingContact.value.city = customer.suburb + ', ' + customer.city
    } else if (customer.suburb) {
      billingContact.value.city = customer.suburb
    } else if (customer.city) {
      billingContact.value.city = customer.city
    }

    if (customer.region) {
      billingContact.value.region = customer.region
    }

    if (customer.firstName) {
      billingContact.value.givenName = customer.firstName
    }

    if (customer.lastName) {
      billingContact.value.familyName = customer.lastName
    }

    if (customer.email) {
      billingContact.value.email = customer.email
    }
  }
})

async function process() {
  const squareToken = await tokenize(card.value)
  const idempotency_key = uuid()

  const params: any = {
    ...integration.value.data?.params,
    squareToken,
    rememberCard: rememberCard.value,
  }

  const verificationToken = await verifyBuyer(paymentsInstance.value, squareToken)

  if (verificationToken) {
    params.verificationToken = verificationToken
  }

  const orderId = props.order.idOrder?.toString() || props.order.objectID?.()
  const response = await $api.integration.processPayment(integration.value.guid, {
    orderId,
    amount: Number(props.amount),
    params,
    idempotencyKey: idempotency_key,
  })

  if (!response) {
    throw new Error($i18n.t('integration.paymentFailed'))
  }

  return await saturate()
}

function load() {
  if ((window as any).Square) {
    nextTick(() => {
      setup()
    })
  } else {
    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.src = ($config as any).public.instance === 'production' ? 'https://web.squarecdn.com/v1/square.js' : 'https://sandbox.web.squarecdn.com/v1/square.js'
    document.body.appendChild(script)

    script.onload = () => {
      nextTick(() => {
        setup()
      })
    }
  }
}

async function setup() {
  const appId = props.option.data.data.authData.applicationID
  const locationId = props.option.data.data.params.location_id

  try {
    paymentsInstance.value = (window as any).Square.payments(appId, locationId)

    const c = await initializeCard(paymentsInstance.value)
    card.value = c
    loaded.value = true

    setTimeout(async () => {
      await card.value?.recalculateSize()
    }, 500)
  } catch (e) {
    console.log(e)
    error.value = $i18n.t('integration.square.error')
  }
}

async function initializeCard(payments: any) {
  const c = await payments.card({
    style: { input: { fontSize: '13px' } },
  })
  await c.attach('#card-container')
  return c
}

async function tokenize(paymentMethod: any) {
  const tokenResult = await paymentMethod.tokenize()
  if (tokenResult.status === 'OK') {
    return tokenResult.token
  } else {
    throw new Error(tokenResult.status)
  }
}

async function verifyBuyer(payments: any, token: string) {
  const intent = 'CHARGE'
  const amount = props.amount.toString()
  const currencyCode = props.option.data.data.params.currency

  const verificationDetails = {
    amount,
    billingContact: billingContact.value,
    currencyCode,
    intent,
  }

  const verificationResults = await payments.verifyBuyer(token, verificationDetails)
  return verificationResults.token
}

async function saturate() {
  const response = await $api.order.get(String(props.order.idOrder))
  return response.data
}

defineExpose({ process })
</script>
